import { createFileRoute } from '@tanstack/react-router'
import { History, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useAuthStore } from '@/shared/auth/auth-store'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/configuracoes/usuarios')({
  component: UsuariosPage,
})

/** Papel exibido a partir das roles do JWT (matriz RBAC do backend). */
function papelDe(roles: readonly string[]): 'admin' | 'editor' | 'member' {
  if (roles.includes('finance_admin')) return 'admin'
  if (roles.includes('finance_writer')) return 'editor'
  return 'member'
}

function UsuariosPage() {
  const { t } = useTranslation(['settings', 'nav', 'common'])
  const name = useAuthStore((s) => s.name)
  const email = useAuthStore((s) => s.email)
  const subject = useAuthStore((s) => s.subject)
  const roles = useAuthStore((s) => s.roles)

  const comingSoon = t('common:comingSoon.title')
  const nome = name ?? email ?? subject ?? ''
  const papel = papelDe(roles)

  const acoes = (
    <>
      {/* Histórico de convites e Convidar usuários vivem no Keycloak (sem API de
          browser cross-tenant) → desabilitados "Em breve". */}
      <Button variant="link" disabled aria-disabled title={comingSoon}>
        <History aria-hidden />
        {t('settings:users.history')}
        <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
      </Button>
      <Button variant="primary" disabled aria-disabled title={comingSoon}>
        <Mail aria-hidden />
        {t('settings:users.invite')}
        <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
      </Button>
    </>
  )

  return (
    <>
      <PageHeader title={t('nav:settings.users')} actions={acoes} />

      <div className="p-6">
        <p className="text-ink-muted mb-3 text-sm">
          {t('common:pagination.showing', { count: 1 })}
        </p>

        <div className="border-line overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-ink-muted">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t('settings:users.columns.name')}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t('settings:users.columns.email')}
                </th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              <tr className="border-line border-t">
                <td className="text-ink px-4 py-3">
                  <span className="inline-flex items-center gap-2">
                    {nome}
                    <Badge tone="info">{t('settings:users.you')}</Badge>
                  </span>
                </td>
                <td className="text-ink-muted px-4 py-3">{email ?? '—'}</td>
                <td className="px-4 py-3 text-right">
                  <Badge tone="neutral">{t(`settings:users.roles.${papel}`)}</Badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-ink-subtle mt-3 text-xs">{t('settings:users.note')}</p>
      </div>
    </>
  )
}
